import _, { isEqual } from 'lodash';
import { LOGGED_OUT } from 'actions/types';
import { sortAccordingToTimestamp } from 'utils/sort';
import { filterActivities, getGenericActivityType } from '../utils';
import { SellOrderType, DividendSubType, } from '../types';
import { getActivityStatusFromProductTransferStatus, getAnalystDetails, getParsedAnalystData } from '../utils/format';
import { GET_ACTIVITIES_FAILURE, GET_ACTIVITIES_REQUEST, GET_ACTIVITIES_SUCCESS, GET_AUTOINVEST_ACTIVITY_REQUEST, GET_AUTOINVEST_ACTIVITY_FAILURE, GET_AUTOINVEST_ACTIVITY_SUCCESS, PAUSE_AUTOINVEST_PLAN_SUCCESS, SKIP_AUTOINVEST_PAYMENT_SUCCESS, UNSKIP_AUTOINVEST_PAYMENT_SUCCESS, GET_PRODUCT_TRANSFER_SUCCESS, GET_SEARCH_RESULTS_SUCCESS, REMOVE_ASSET_ID_TO_FETCH_REAL_TIME_PRICES, SET_ASSET_ID_TO_FETCH_REAL_TIME_PRICES, } from '../actions/types';
import { ALL_STOCKS_KEY, stockScreenDefaultEventTypes } from '../constants';
import { getEventsNearToToday } from './selectors';
const genericActivityToKeyMap = {
    All: 'activityCacheForTradingAccount',
    Orders: 'orderActivityCacheForTradingAccount',
    Interest: 'interestActivityCacheForTradingAccount',
    Dividends: 'dividendActivityCacheForTradingAccount',
    Transactions: 'transactionActivityCacheForTradingAccount',
};
export const initialState = {
    filledDayTrades: undefined,
    pendingDayTrades: undefined,
    recurringBuys: [],
    recurringBuysForAssetId: {},
    historyForRecurringBuyId: {},
    pendingStockAlerts: {},
    isErrorInFetchingInvestAcc: undefined,
    isFetchingWealthAccounts: false,
    isMarketClosed: false,
};
export const removeFirstOccurrenceOfSymbol = (arr, symbol) => {
    if (arr?.length) {
        const index = arr.findIndex((el) => el === symbol);
        if (index !== -1) {
            return [...arr.slice(0, index), ...arr.slice(index + 1)];
        }
    }
    return arr;
};
const unpersistedInvestReducer = (state = initialState, action) => {
    switch (action.type) {
        case 'GET_TRADING_MARKETS_INFO_SUCCESS': {
            const USMarket = action.payload.markets.find((market) => market.name === 'US' && market.type === 'stocks');
            return {
                ...state,
                isMarketClosed: USMarket ? USMarket.info.status === 'closed' : false,
            };
        }
        case 'UPDATE_WEALTH_ACCOUNTS': {
            return {
                ...state,
                isFetchingWealthAccounts: true,
            };
        }
        case 'UPDATE_WEALTH_ACCOUNTS_COMPLETE': {
            return {
                ...state,
                isFetchingWealthAccounts: false,
            };
        }
        case 'INVEST_GET_COMPACT_TRADING_ACTIVITY_SUCCESS': {
            return {
                ...state,
                compactActivityForAssetId: {
                    ...state.compactActivityForAssetId,
                    [action.extra.assetId]: action.payload.activities,
                },
            };
        }
        case GET_ACTIVITIES_REQUEST: {
            const { assetIds, accountId, paging, types } = action.extra;
            if (!assetIds?.length) {
                const genericType = getGenericActivityType(types);
                const key = `${accountId}-${genericType}`;
                const currentPaginationToken = state.requestingActivitiesPaginationTokenForTradingAccount?.[key];
                return {
                    ...state,
                    requestingActivitiesForTradingAccount: {
                        ...state.requestingActivitiesForTradingAccount,
                        [key]: true,
                    },
                    requestingActivitiesPaginationTokenForTradingAccount: {
                        ...state.requestingActivitiesPaginationTokenForTradingAccount,
                        [key]: 'page' in paging && paging.page === 1 ? undefined : currentPaginationToken,
                    },
                };
            }
            return state;
        }
        case GET_ACTIVITIES_FAILURE: {
            const { assetIds, accountId, types } = action.extra;
            if (!assetIds?.length) {
                const genericType = getGenericActivityType(types);
                const key = `${accountId}-${genericType}`;
                const requestingActivitiesForTradingAccount = {
                    ...state.requestingActivitiesForTradingAccount,
                };
                delete requestingActivitiesForTradingAccount[key];
                return {
                    ...state,
                    requestingActivitiesForTradingAccount,
                };
            }
            return state;
        }
        case GET_ACTIVITIES_SUCCESS: {
            const { payload, extra } = action;
            const { paging } = payload;
            const { accountId, types, assetIds, paging: initialPaging } = extra;
            if (!assetIds?.length) {
                // This is the case for generic activities
                // we store all the activities and paginate them here
                const isFirstPage = 'page' in paging && paging.page === 1;
                const genericType = getGenericActivityType(types);
                const key = `${accountId}-${genericType}`;
                const genericActivityType = getGenericActivityType(assetIds?.length ? undefined : types);
                const stateKey = genericActivityToKeyMap[genericActivityType];
                const currentPaginationToken = state.requestingActivitiesPaginationTokenForTradingAccount?.[key];
                if (currentPaginationToken !== ('token' in initialPaging ? initialPaging.token : undefined)) {
                    return state;
                }
                const requestingActivitiesForTradingAccount = {
                    ...state.requestingActivitiesForTradingAccount,
                };
                delete requestingActivitiesForTradingAccount[key];
                const newActivities = [];
                const pendingActivities = [];
                payload.activities.forEach((activity) => {
                    if (isFirstPage && 'status' in activity && activity.status === 'pending' && !types) {
                        pendingActivities.push(activity);
                    }
                    if (!('status' in activity) || activity.status !== 'failed' || activity.type === 'isa_transfer_in') {
                        newActivities.push(activity);
                    }
                });
                return {
                    ...state,
                    [stateKey]: {
                        ...state[stateKey],
                        [accountId]: !currentPaginationToken
                            ? newActivities
                            : [...(state[stateKey]?.[accountId] || []), ...newActivities],
                    },
                    requestingActivitiesForTradingAccount,
                    pendingActivitiesForTradingAccount: isFirstPage && !types
                        ? { ...state.pendingActivitiesForTradingAccount, [accountId]: pendingActivities }
                        : state.pendingActivitiesForTradingAccount,
                    requestingActivitiesPaginationTokenForTradingAccount: {
                        ...state.requestingActivitiesPaginationTokenForTradingAccount,
                        [key]: payload.activities.length < 30 ? null : payload.paging.next || null,
                    },
                };
            }
            const isFirstPage = 'page' in paging && paging.page === 1;
            // we only support 1 asset id at a time for now
            if (isFirstPage) {
                return {
                    ...state,
                    activityCacheForAssetId: {
                        ...state.activityCacheForAssetId,
                        [assetIds[0]]: payload.activities.filter((activity) => 'status' in activity &&
                            activity.status !== 'pending' &&
                            (activity.status !== 'failed' || activity.type === 'isa_transfer_in')),
                    },
                };
            }
            return state;
        }
        case SET_ASSET_ID_TO_FETCH_REAL_TIME_PRICES: {
            return {
                ...state,
                realTimePricesAssetIds: [...(state.realTimePricesAssetIds || []), action.assetId],
            };
        }
        case REMOVE_ASSET_ID_TO_FETCH_REAL_TIME_PRICES: {
            return {
                ...state,
                realTimePricesAssetIds: removeFirstOccurrenceOfSymbol(state.realTimePricesAssetIds, action.assetId),
            };
        }
        case 'INVEST_GET_CORPORATE_EVENTS_REQUEST': {
            if (action.extra.calendarKey === 'portfolio') {
                const portfolioObj = state.investmentCalendar?.portfolio;
                return {
                    ...state,
                    loadingPortfolioCalendar: true,
                    portfolioCalendarFilter: action.extra.types,
                    loadingPortfolioCalendarToken: action.extra.futureToken,
                    ...((action.extra.types || (!action.extra.types && state.portfolioCalendarFilter)) &&
                        !action.extra.futureToken &&
                        portfolioObj && {
                        investmentCalendar: {
                            ...state.investmentCalendar,
                            portfolio: {
                                ...portfolioObj,
                                events: [],
                                futureToken: '',
                            },
                        },
                    }),
                };
            }
            const dataObj = state.investmentCalendar?.[action.extra.calendarKey];
            return {
                ...state,
                calendarFilterForAssetId: action.extra.types,
                loadingCalendarForAssetId: action.extra.calendarKey,
                loadingCalendarForAssetToken: action.extra.futureToken,
                ...(!isEqual(action.extra.types, state.calendarFilterForAssetId) &&
                    !action.extra.futureToken &&
                    dataObj && {
                    investmentCalendar: {
                        ...state.investmentCalendar,
                        [action.extra.calendarKey]: {
                            ...dataObj,
                            events: [],
                            futureToken: '',
                        },
                    },
                }),
            };
        }
        case 'INVEST_GET_CORPORATE_EVENTS_FAILURE': {
            if (action.extra.calendarKey === 'portfolio' &&
                action.extra.futureToken === state.loadingPortfolioCalendarToken &&
                _.isEqual(action.extra.types, state.portfolioCalendarFilter)) {
                return {
                    ...state,
                    loadingPortfolioCalendar: false,
                };
            }
            if (action.extra.calendarKey === state.loadingCalendarForAssetId &&
                action.extra.futureToken === state.loadingCalendarForAssetToken &&
                _.isEqual(action.extra.types, state.calendarFilterForAssetId)) {
                return {
                    ...state,
                    loadingCalendarForAssetId: undefined,
                    loadingCalendarForAssetToken: undefined,
                };
            }
            return state;
        }
        case 'INVEST_GET_CORPORATE_EVENTS_SUCCESS': {
            const parsedEvents = action.payload.events.map((event) => {
                if (event.type === 'DIVIDEND' &&
                    (event.subType === DividendSubType.EX_CASH || event.subType === DividendSubType.EX_STOCK)) {
                    return {
                        ...event,
                        type: 'EX_DIVIDEND',
                    };
                }
                return event;
            });
            const usedFutureToken = action.extra.futureToken;
            if (action.extra.calendarKey === 'portfolio' &&
                action.extra.futureToken === state.loadingPortfolioCalendarToken &&
                _.isEqual(action.extra.types, state.portfolioCalendarFilter)) {
                const currObj = state.investmentCalendar?.portfolio;
                return {
                    ...state,
                    investmentCalendar: {
                        ...state.investmentCalendar,
                        portfolio: {
                            events: usedFutureToken ? [...(currObj?.events || []), ...parsedEvents] : parsedEvents,
                            futureToken: action.payload.futureToken,
                            eventsForSection: usedFutureToken || action.extra.types
                                ? currObj?.eventsForSection || []
                                : getEventsNearToToday(parsedEvents, true),
                        },
                    },
                    loadingPortfolioCalendar: false,
                    loadingPortfolioCalendarToken: undefined,
                };
            }
            if (state.loadingCalendarForAssetId === action.extra.calendarKey &&
                action.extra.futureToken === state.loadingCalendarForAssetToken &&
                _.isEqual(action.extra.types, state.calendarFilterForAssetId)) {
                const currObj = state.investmentCalendar?.[action.extra.calendarKey];
                return {
                    ...state,
                    investmentCalendar: {
                        ...state.investmentCalendar,
                        [action.extra.calendarKey]: {
                            futureToken: action.payload.futureToken,
                            eventsForSection: !usedFutureToken && isEqual(action.extra.types, stockScreenDefaultEventTypes)
                                ? getEventsNearToToday(parsedEvents, false)
                                : currObj?.eventsForSection || [],
                            events: usedFutureToken ? [...(currObj?.events || []), ...parsedEvents] : parsedEvents,
                        },
                    },
                    loadingCalendarForAssetId: undefined,
                    loadingCalendarForAssetToken: undefined,
                };
            }
            return state;
        }
        case 'GET_ORDERS_REQUEST': {
            // case for limit/stop orders
            if (action.extra.orderTypes.includes(SellOrderType.LIMIT) ||
                action.extra.orderTypes.includes(SellOrderType.STOP)) {
                // case for portfolio list
                if (!action.extra.assetIds?.length) {
                    return {
                        ...state,
                        requestingLimitStopOrders: true,
                        requestingLimitStopOrdersPaginationToken: action.extra.paging?.token,
                    };
                }
                // case for list for a stock
                return {
                    ...state,
                    requestingLimitStopOrdersForAssetId: true,
                    requestingLimitStopOrdersAssetId: action.extra.assetIds[0],
                    requestingLimitStopOrdersPaginationTokenForAssetId: action.extra.paging?.token,
                };
            }
            // case for portfolio list
            if (!action.extra.assetIds?.length) {
                return {
                    ...state,
                    requestingMarketOrders: true,
                    requestingMarketOrdersPaginationToken: action.extra.paging?.token,
                };
            }
            // case for list for a stock
            return {
                ...state,
                requestingMarketOrdersForAssetId: true,
                requestingMarketOrdersAssetId: action.extra.assetIds[0],
                requestingMarketOrdersPaginationTokenForAssetId: action.extra.paging?.token,
            };
        }
        case 'GET_ORDERS_SUCCESS': {
            // case for limit/stop orders
            if (action.extra.orderTypes.includes(SellOrderType.LIMIT) ||
                action.extra.orderTypes.includes(SellOrderType.STOP)) {
                // case for portfolio list
                if (!action.extra.assetIds?.length) {
                    return {
                        ...state,
                        requestingLimitStopOrders: false,
                        requestingLimitStopOrdersPaginationToken: action.payload.paging.next,
                    };
                }
                // case for list for a stock
                return {
                    ...state,
                    requestingLimitStopOrdersForAssetId: false,
                    requestingLimitStopOrdersPaginationTokenForAssetId: action.payload.paging.next,
                };
            }
            // case for portfolio list
            if (!action.extra.assetIds?.length) {
                return {
                    ...state,
                    requestingMarketOrders: false,
                    requestingMarketOrdersPaginationToken: action.payload.paging.next,
                };
            }
            // case for list for a stock
            return {
                ...state,
                requestingMarketOrdersForAssetId: false,
                requestingMarketOrdersPaginationTokenForAssetId: action.payload.paging.next,
            };
        }
        case 'GET_ORDERS_FAILURE': {
            // case for limit/stop orders
            if (action.extra.orderTypes.includes(SellOrderType.LIMIT) ||
                action.extra.orderTypes.includes(SellOrderType.STOP)) {
                // case for portfolio list
                if (!action.extra.assetIds?.length) {
                    return {
                        ...state,
                        requestingLimitStopOrders: false,
                    };
                }
                // case for list for a stock
                return {
                    ...state,
                    requestingLimitStopOrdersForAssetId: false,
                };
            }
            // case for portfolio list
            if (!action.extra.assetIds?.length) {
                return {
                    ...state,
                    requestingMarketOrders: false,
                };
            }
            // case for list for a stock
            return {
                ...state,
                requestingMarketOrdersForAssetId: false,
            };
        }
        case 'GET_TRANSFER_PROVIDERS_REQUEST': {
            const key = `${action.extra.accountType}-${action.extra.type}`;
            return {
                ...state,
                transferProvidersCache: {
                    ...state.transferProvidersCache,
                    [key]: { providers: state.transferProvidersCache?.[key]?.providers ?? [], isFetching: true },
                },
            };
        }
        case 'GET_TRANSFER_PROVIDERS_SUCCESS': {
            const key = `${action.extra.accountType}-${action.extra.type}`;
            return {
                ...state,
                transferProvidersCache: {
                    ...state.transferProvidersCache,
                    [key]: { providers: action.payload.transferProviders, isFetching: false },
                },
            };
        }
        case 'GET_TRANSFER_PROVIDERS_FAILURE': {
            const key = `${action.extra.accountType}-${action.extra.type}`;
            return {
                ...state,
                transferProvidersCache: {
                    ...state.transferProvidersCache,
                    [key]: { providers: state.transferProvidersCache?.[key]?.providers ?? [], isFetching: false },
                },
            };
        }
        case 'INVEST_GET_ANALYST_INFO_SUCCESS_ACTION': {
            const analysisInfo = getParsedAnalystData(action.payload.analystInfo);
            if (analysisInfo) {
                return {
                    ...state,
                    analystInfoMap: {
                        ...state.analystInfoMap,
                        [action.extra.assetId]: analysisInfo,
                    },
                };
            }
            return state;
        }
        case 'INVEST_GET_TRIGGERED_STOCK_ALERTS_REQUEST_ACTION': {
            return {
                ...state,
                requestingTriggeredStockAlerts: true,
                triggeredStockAlertsPaginationAssetId: action.extra.assetId,
                triggeredStockAlertsPaginationToken: action.extra.paginationToken,
            };
        }
        case 'INVEST_GET_TRIGGERED_STOCK_ALERTS_SUCCESS_ACTION': {
            if (state.triggeredStockAlertsPaginationAssetId === action.extra.assetId) {
                return {
                    ...state,
                    triggeredStockAlerts: {
                        ...state.triggeredStockAlerts,
                        [action.extra.assetId]: action.payload.paging.page === 1
                            ? action.payload.alerts
                            : [...(state.triggeredStockAlerts?.[action.extra.assetId] || []), ...action.payload.alerts],
                    },
                    triggeredStockAlertsPaginationToken: action.payload.paging?.next,
                    requestingTriggeredStockAlerts: false,
                };
            }
            return state;
        }
        case 'INVEST_GET_TRIGGERED_STOCK_ALERTS_FAILURE_ACTION': {
            if (action.extra.assetId === state.triggeredStockAlertsPaginationAssetId) {
                return {
                    ...state,
                    requestingTriggeredStockAlerts: false,
                };
            }
            return state;
        }
        case 'INVEST_GET_STOCK_ALERTS_REQUEST_ACTION': {
            if (action.extra.assetId === ALL_STOCKS_KEY) {
                return {
                    ...state,
                    requestingAllPendingStockAlerts: true,
                    allPendingStockAlertsPaginationToken: action.extra.paginationToken,
                };
            }
            return {
                ...state,
                requestingPendingStockAlerts: true,
                pendingStockAlertsPaginationAssetId: action.extra.assetId,
                pendingStockAlertsPaginationToken: action.extra.paginationToken,
            };
        }
        case 'INVEST_GET_STOCK_ALERTS_FAILURE_ACTION': {
            if (action.extra.assetId === ALL_STOCKS_KEY) {
                return {
                    ...state,
                    requestingAllPendingStockAlerts: false,
                };
            }
            if (action.extra.assetId === state.pendingStockAlertsPaginationAssetId) {
                return {
                    ...state,
                    requestingPendingStockAlerts: false,
                };
            }
            return state;
        }
        case 'INVEST_GET_STOCK_ALERTS_SUCCESS_ACTION': {
            if (action.extra.assetId === ALL_STOCKS_KEY) {
                return {
                    ...state,
                    pendingStockAlerts: {
                        ...state.pendingStockAlerts,
                        [action.extra.assetId]: action.payload.paging.page === 1
                            ? action.payload.alerts
                            : [...(state.pendingStockAlerts?.[action.extra.assetId] || []), ...action.payload.alerts],
                    },
                    requestingAllPendingStockAlerts: false,
                    allPendingStockAlertsPaginationToken: action.payload.paging?.next,
                };
            }
            if (state.pendingStockAlertsPaginationAssetId === action.extra.assetId) {
                return {
                    ...state,
                    pendingStockAlerts: {
                        ...state.pendingStockAlerts,
                        [action.extra.assetId]: action.payload.paging.page === 1
                            ? action.payload.alerts
                            : [...(state.pendingStockAlerts?.[action.extra.assetId] || []), ...action.payload.alerts],
                    },
                    requestingPendingStockAlerts: false,
                    pendingStockAlertsPaginationToken: action.payload.paging?.next,
                };
            }
            return state;
        }
        case 'INVEST_EDIT_STOCK_ALERT_SUCCESS_ACTION': {
            return {
                ...state,
                pendingStockAlerts: {
                    ...state.pendingStockAlerts,
                    [action.extra.assetId]: (state.pendingStockAlerts?.[action.extra.assetId] || []).map((alert) => {
                        if (alert.alertId === action.extra.id) {
                            return action.payload.alert;
                        }
                        return alert;
                    }),
                    ALL_STOCKS: (state.pendingStockAlerts?.[ALL_STOCKS_KEY] || []).map((alert) => {
                        if (alert.alertId === action.extra.id) {
                            return action.payload.alert;
                        }
                        return alert;
                    }),
                },
            };
        }
        case 'INVEST_CREATE_STOCK_ALERT_SUCCESS_ACTION': {
            return {
                ...state,
                pendingStockAlerts: {
                    ...state.pendingStockAlerts,
                    [action.extra.assetId]: [action.payload.alert, ...(state.pendingStockAlerts?.[action.extra.assetId] || [])],
                    ALL_STOCKS: [action.payload.alert, ...(state.pendingStockAlerts?.[ALL_STOCKS_KEY] || [])],
                },
            };
        }
        case 'INVEST_DELETE_STOCK_ALERT_SUCCESS': {
            const newAllStocksArr = state.pendingStockAlerts?.[ALL_STOCKS_KEY]?.filter((stockAlert) => stockAlert.alertId !== action.extra.alertId);
            const assetIdArr = state.pendingStockAlerts?.[action.extra.assetId]?.filter((stockAlert) => stockAlert.alertId !== action.extra.alertId);
            return {
                ...state,
                pendingStockAlerts: {
                    ...state.pendingStockAlerts,
                    ALL_STOCKS: newAllStocksArr,
                    [action.extra.assetId]: assetIdArr,
                },
            };
        }
        case 'GET_STOCKS_META_SUCCESS': {
            const symbolAssetIdMap = action.payload.companies.reduce((prev, curr) => {
                if (!curr.symbol)
                    return prev;
                return {
                    ...prev,
                    [curr.symbol]: curr.assetId,
                };
            }, {});
            return {
                ...state,
                symbolAssetIdMap: {
                    ...state.symbolAssetIdMap,
                    ...symbolAssetIdMap,
                },
            };
        }
        case 'GET_ANALYST_RATINGS_DETAILS_SUCCESS_ACTION': {
            const analystDetails = getAnalystDetails(action.payload.analystInfo);
            return {
                ...state,
                analystInfoDetailsMap: {
                    ...state.analystInfoDetailsMap,
                    [action.payload.assetId]: analystDetails,
                },
            };
        }
        case 'GET_TRADING_ACCOUNTS_SUCCESS': {
            return {
                ...state,
                isErrorInFetchingInvestAcc: false,
            };
        }
        case 'GET_PATTERN_DAY_TRADING_INFO_SUCCESS': {
            return {
                ...state,
                filledDayTrades: action.payload.filledDayTrades,
                pendingDayTrades: action.payload.pendingDayTrades,
            };
        }
        case 'GET_PATTERN_DAY_TRADING_INFO_FAILURE': {
            return {
                ...state,
                filledDayTrades: undefined,
                pendingDayTrades: undefined,
            };
        }
        case 'GET_TRADING_ACCOUNTS_FAILURE': {
            return {
                ...state,
                isErrorInFetchingInvestAcc: true,
            };
        }
        case 'GET_RECURRING_BUYS_SUCCESS': {
            if (action.extra) {
                return {
                    ...state,
                    recurringBuysForAssetId: {
                        ...state.recurringBuysForAssetId,
                        [action.extra]: action.payload.recurringBuys,
                    },
                };
            }
            return {
                ...state,
                recurringBuys: action.payload.recurringBuys,
            };
        }
        case 'DELETE_RECURRING_BUY_SUCCESS': {
            const { id, assetId } = action.extra;
            return {
                ...state,
                recurringBuys: state.recurringBuys?.filter((recurringBuy) => recurringBuy.id !== id),
                recurringBuysForAssetId: {
                    ...state.recurringBuysForAssetId,
                    [assetId]: state.recurringBuysForAssetId?.[assetId]?.filter((recurringBuy) => recurringBuy.id !== id) || [],
                },
            };
        }
        case 'UPDATE_RECURRING_BUY_SUCCESS': {
            const { recurringBuy } = action.payload;
            return {
                ...state,
                recurringBuys: state.recurringBuys?.map((oldRecurringBuy) => {
                    if (oldRecurringBuy.id === recurringBuy.id) {
                        return recurringBuy;
                    }
                    return oldRecurringBuy;
                }),
                recurringBuysForAssetId: {
                    ...state.recurringBuysForAssetId,
                    [recurringBuy.assetId]: state.recurringBuysForAssetId?.[recurringBuy.assetId]?.map((oldRecurringBuy) => {
                        if (oldRecurringBuy.id === recurringBuy.id) {
                            return recurringBuy;
                        }
                        return oldRecurringBuy;
                    }) || [],
                },
            };
        }
        case 'GET_RECURRING_BUY_ACTIVITIES_SUCCESS': {
            const { id } = action.extra;
            const { activities, paging } = action.payload;
            const filteredActivities = activities.filter(filterActivities);
            if (paging.page === 1) {
                return {
                    ...state,
                    historyForRecurringBuyId: {
                        ...state.historyForRecurringBuyId,
                        [id]: filteredActivities,
                    },
                };
            }
            return {
                ...state,
                historyForRecurringBuyId: {
                    ...state.historyForRecurringBuyId,
                    [id]: _.uniqBy(state.historyForRecurringBuyId?.[id]?.concat(filteredActivities) ?? filteredActivities, (i) => i.id).sort(sortAccordingToTimestamp),
                },
            };
        }
        case 'SET_SELECTED_TRADING_ACCOUNT_ID': {
            return {
                ...state,
                selectedTradingAccountId: action.accountId,
            };
        }
        case GET_SEARCH_RESULTS_SUCCESS: {
            if (action.extra.broker === 'seccl' &&
                (action.extra.currentPage === 1 || !action.extra.currentPage) &&
                !action.extra.searchTerm) {
                return {
                    ...state,
                    secclAssets: action.payload.assets,
                };
            }
            return state;
        }
        case GET_PRODUCT_TRANSFER_SUCCESS: {
            const { tradingAccountId } = action.extra;
            const latestProviderStatus = action.payload.providerStatus;
            const pendingActivitiesForTradingAccount = state.pendingActivitiesForTradingAccount?.[tradingAccountId];
            const newProductTransferCache = {
                ...state.productTransferCache,
                [action.payload.id]: action.payload,
            };
            if (!(!latestProviderStatus ||
                latestProviderStatus === 'WITH_PREVIOUS_PROVIDER' ||
                latestProviderStatus === 'REVIEWING_DOCUMENTATION' ||
                latestProviderStatus === 'ONBOARDING_ASSETS') &&
                pendingActivitiesForTradingAccount?.length) {
                const transactionActivitiesForTradingAccount = state.transactionActivityCacheForTradingAccount?.[tradingAccountId];
                const allActivitiesForTradingAccount = state.activityCacheForTradingAccount?.[tradingAccountId];
                const { activities: pendingActivities, activity } = pendingActivitiesForTradingAccount.reduce((prev, curr) => {
                    if (curr.id === action.payload.id && curr.type === 'isa_transfer_in') {
                        return {
                            ...prev,
                            activity: curr,
                        };
                    }
                    return {
                        ...prev,
                        activities: [...prev.activities, curr],
                    };
                }, {
                    activities: [],
                    activity: undefined,
                });
                const updatedActivity = activity
                    ? {
                        ...activity,
                        status: getActivityStatusFromProductTransferStatus(latestProviderStatus),
                    }
                    : undefined;
                const updatedTransactionActivitiesForTradingAccount = transactionActivitiesForTradingAccount?.length && updatedActivity
                    ? [
                        ...transactionActivitiesForTradingAccount.filter((activity) => activity.id !== action.payload.id),
                        updatedActivity,
                    ].sort(sortAccordingToTimestamp)
                    : transactionActivitiesForTradingAccount;
                const updatedAllActivitiesForTradingAccount = allActivitiesForTradingAccount?.length && updatedActivity
                    ? [
                        ...allActivitiesForTradingAccount.filter((activity) => activity.id !== action.payload.id),
                        updatedActivity,
                    ].sort(sortAccordingToTimestamp)
                    : allActivitiesForTradingAccount;
                return {
                    ...state,
                    productTransferCache: newProductTransferCache,
                    pendingActivitiesForTradingAccount: {
                        ...state.pendingActivitiesForTradingAccount,
                        [tradingAccountId]: pendingActivities,
                    },
                    transactionActivityCacheForTradingAccount: {
                        ...state.transactionActivityCacheForTradingAccount,
                        [tradingAccountId]: updatedTransactionActivitiesForTradingAccount,
                    },
                    activityCacheForTradingAccount: {
                        ...state.activityCacheForTradingAccount,
                        [tradingAccountId]: updatedAllActivitiesForTradingAccount,
                    },
                };
            }
            return {
                ...state,
                productTransferCache: newProductTransferCache,
            };
        }
        case GET_AUTOINVEST_ACTIVITY_REQUEST: {
            const { accountId, paging } = action.extra;
            const currentPaginationToken = state.requestingAutoinvestActivitiesPaginationTokenForTradingAccount?.[accountId];
            return {
                ...state,
                requestingAutoinvestActivitiesForTradingAccount: {
                    ...state.requestingAutoinvestActivitiesForTradingAccount,
                    [accountId]: true,
                },
                requestingAutoinvestActivitiesPaginationTokenForTradingAccount: {
                    ...state.requestingAutoinvestActivitiesPaginationTokenForTradingAccount,
                    [accountId]: 'page' in paging && paging.page === 1 ? undefined : currentPaginationToken,
                },
            };
        }
        case GET_AUTOINVEST_ACTIVITY_SUCCESS: {
            const { accountId, paging: initialPaging } = action.extra;
            const currentPaginationToken = state.requestingAutoinvestActivitiesPaginationTokenForTradingAccount?.[accountId];
            if (currentPaginationToken !== ('token' in initialPaging ? initialPaging.token : undefined)) {
                return state;
            }
            const isFirstPage = 'page' in action.extra.paging;
            const prev = state.autoInvestActivitiesForAccount?.[accountId];
            const incoming = action.payload.activities;
            const requestingAutoinvestActivitiesForTradingAccount = {
                ...state.requestingAutoinvestActivitiesForTradingAccount,
            };
            delete requestingAutoinvestActivitiesForTradingAccount[accountId];
            return {
                ...state,
                requestingAutoinvestActivitiesForTradingAccount,
                autoInvestActivitiesForAccount: {
                    ...state.autoInvestActivitiesForAccount,
                    [accountId]: isFirstPage ? incoming : [...(prev ?? []), ...incoming],
                },
                requestingAutoinvestActivitiesPaginationTokenForTradingAccount: {
                    ...state.requestingAutoinvestActivitiesPaginationTokenForTradingAccount,
                    [accountId]: action.payload.activities.length < 30 ? null : action.payload.paging?.next || null,
                },
            };
        }
        case PAUSE_AUTOINVEST_PLAN_SUCCESS: {
            const { skipPayment, activityId } = action.extra;
            if (!skipPayment || !activityId)
                return state;
            const { accountId } = action.payload.plan;
            const activities = state.autoInvestActivitiesForAccount?.[accountId];
            if (!activities)
                return state;
            const updated = activities.map((a) => (a.id === activityId ? { ...a, status: 'skipped' } : a));
            return {
                ...state,
                autoInvestActivitiesForAccount: {
                    ...state.autoInvestActivitiesForAccount,
                    [accountId]: updated,
                },
            };
        }
        case SKIP_AUTOINVEST_PAYMENT_SUCCESS: {
            const { accountId, activityId } = action.extra;
            const activities = state.autoInvestActivitiesForAccount?.[accountId];
            if (!activities)
                return state;
            const updated = activities.map((a) => (a.id === activityId ? { ...a, status: 'skipped' } : a));
            return {
                ...state,
                autoInvestActivitiesForAccount: {
                    ...state.autoInvestActivitiesForAccount,
                    [accountId]: updated,
                },
            };
        }
        case UNSKIP_AUTOINVEST_PAYMENT_SUCCESS: {
            const { accountId, activityId } = action.extra;
            const activities = state.autoInvestActivitiesForAccount?.[accountId];
            if (!activities)
                return state;
            const updated = activities.map((a) => (a.id === activityId ? { ...a, status: 'upcoming' } : a));
            return {
                ...state,
                autoInvestActivitiesForAccount: {
                    ...state.autoInvestActivitiesForAccount,
                    [accountId]: updated,
                },
            };
        }
        case GET_AUTOINVEST_ACTIVITY_FAILURE: {
            const { accountId } = action.extra;
            const requestingAutoinvestActivitiesForTradingAccount = {
                ...state.requestingAutoinvestActivitiesForTradingAccount,
            };
            delete requestingAutoinvestActivitiesForTradingAccount[accountId];
            return {
                ...state,
                requestingAutoinvestActivitiesForTradingAccount,
            };
        }
        case 'SET_HMRC_APP_INSTALLED': {
            return {
                ...state,
                isHmrcAppInstalled: action.isInstalled,
            };
        }
        case LOGGED_OUT: {
            return initialState;
        }
        default: {
            return state;
        }
    }
};
export default unpersistedInvestReducer;
